import { useFpCampaignCartService } from '@cart-services/hooks/useFpCampaignCartService'
import { type CountryCode, type ProjectCode } from '@constants'
import { sumOfArray } from '@utils'
import { createContext, useMemo, type PropsWithChildren } from 'react'

interface ProductCatalogContextProps extends ProductCatalogProviderProps {
  cartTotalQuantity: number
  cartTotalPrice: number
  cartFrozenTotalPrice: number
  cartNormalTotalPrice: number
  currency?: string
}

export const ProductCatalogContext = createContext<ProductCatalogContextProps | null>(null)

export interface ProductCatalogProviderProps {
  countryCode: CountryCode
  projectCode: ProjectCode
  campaignId: number
  campaignCode: string
  currency?: string
}

export const ProductCatalogProvider = (props: PropsWithChildren<ProductCatalogProviderProps>) => {
  const { children, countryCode, projectCode, currency, campaignId, campaignCode } = props
  const { items: cartItems } = useFpCampaignCartService(campaignId)

  const cartTotalQuantity = useMemo(() => sumOfArray(cartItems, (acc, { quantity = 0 }) => acc + quantity), [cartItems])

  const normalCartItems = useMemo(() => cartItems.filter(item => item.delivery_class === 'normal'), [cartItems])
  const frozenCartItems = useMemo(() => cartItems.filter(item => item.delivery_class === 'frozen'), [cartItems])

  const cartTotalPrice = useMemo(() => sumOfArray(cartItems, (acc, { sale_price = 0, quantity = 0 }) => acc + sale_price * quantity), [cartItems])
  const cartNormalTotalPrice = useMemo(() => sumOfArray(normalCartItems, (acc, { sale_price = 0, quantity = 0 }) => acc + sale_price * quantity), [normalCartItems])
  const cartFrozenTotalPrice = useMemo(() => sumOfArray(frozenCartItems, (acc, { sale_price = 0, quantity = 0 }) => acc + sale_price * quantity), [frozenCartItems])

  const contextValue = useMemo(
    () => ({
      cartTotalQuantity,
      cartTotalPrice,
      cartNormalTotalPrice,
      cartFrozenTotalPrice,
      countryCode,
      projectCode,
      currency,
      campaignId,
      campaignCode,
    }),
    [cartTotalQuantity, cartTotalPrice, cartNormalTotalPrice, cartFrozenTotalPrice, countryCode, projectCode, currency, campaignId, campaignCode],
  )

  return (
    <ProductCatalogContext.Provider value={contextValue}>
      {children}
    </ProductCatalogContext.Provider>
  )
}
